import { useTranslation } from 'react-i18next';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { 
  Box, 
  Divider, 
  IconButton, 
  Modal, 
  Typography, 
  useTheme,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Alert
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@frontend/features/common/types';
import { NimbuxHotel } from '@bandido/shared/interfaces';
import { OrderOptionsValues } from '@bandido/shared/enums';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { useGetHotelNimbuxHotels, useGetHotelVendorHotelById, useGetHotelNimbuxHotelById } from '../../queries';
import { LoadingButton } from '@mui/lab';
import { useLinkHotels } from '../../mutations';
import { LoadingSkeleton } from '../Jobs';

const WideModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80vw',
  maxWidth: 900,
  height: '80vh',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[24],
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

interface VendorHotelLinkModalProps {
  open: boolean;
  onClose: () => void;
  vendorHotelId: number;
}

export const VendorHotelLinkModal = ({ open, onClose, vendorHotelId }: VendorHotelLinkModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useDebouncedState('', 500);
  const queryClient = useQueryClient();

  const { data: vendorHotel, isLoading: isLoadingVendorHotel } = useGetHotelVendorHotelById(vendorHotelId);
  const { data: nimbuxHotel, isLoading: isLoadingNimbuxParent } = useGetHotelNimbuxHotelById(vendorHotel?.nimbuxParentId || -1);
  const { data: nimbuxHotels, isLoading: isLoadingNimbuxHotels } = useGetHotelNimbuxHotels({
    name: searchText,
    currentPage: 1,
    pageSize: 20,
    sort: 'name',
    order: OrderOptionsValues.asc,
  });


  const { mutate: linkHotel, isLoading: isLinking } = useLinkHotels({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotels]);
      queryClient.invalidateQueries([QueryKeys.getHotelVendorHotels]);
      queryClient.invalidateQueries([QueryKeys.getHotelVendorHotelById]);
      queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById]);
    },
  });

  const handleLink = (nimbuxParentId: number) => {
    linkHotel({
      nimbuxParentId,
      vendorHotelId: vendorHotel?.id!,
    });
  };

  const handleUnlink = () => {
    linkHotel({
      nimbuxParentId: null,
      vendorHotelId: vendorHotel?.id!,
    });
  };

  const renderNimbuxHotelItem = (nimbuxHotel: NimbuxHotel) => (
    <ListItem
      key={nimbuxHotel.id}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        mb: 1,
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {nimbuxHotel.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
          <Typography variant="body2" color="text.secondary">
            {nimbuxHotel.city}, {nimbuxHotel.country}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {t('hotelContent.management.vendorHotels.linkModal.nimbuxHotelLabel') || 'Nimbux Hotel:'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {nimbuxHotel.name || t('hotelContent.management.vendorHotels.linkModal.noNimbuxHotel') || 'No tiene'}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ ml: 2 }}>
        <LoadingButton
          loading={isLinking}
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleLink(nimbuxHotel.id!)}
        >
          {t('hotelContent.management.vendorHotels.linkModal.link') || 'Link'}
        </LoadingButton>
      </Box>
    </ListItem>
  );

  console.log('nimbuxHotel', nimbuxHotel);
  console.log('vendorHotel', vendorHotel);
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="vendor-hotel-link-modal-title"
    >
      <WideModalBox>
        {isLoadingNimbuxParent || isLoadingVendorHotel ? (
          <LoadingSkeleton />
        ) : (
          <>
          {/* Header */}
          <Box display="flex" gap={theme.spacing(2)} mb={2}>
            <Box flexGrow={1} display="flex" alignItems="center" gap={theme.spacing(1)}>
              <Typography id="vendor-hotel-link-modal-title" variant="h6" component="h2">
                {vendorHotel?.name}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon color="primary" />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />
          <>
            {/* Current linked nimbux hotel - Always show mockup example */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                {t('hotelContent.management.vendorHotels.linkModal.currentNimbuxHotel')}
              </Typography>

              {nimbuxHotel?.id ? (
                <Alert 
                  severity="info" 
                  action={
                    <LoadingButton
                      loading={isLinking}
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<LinkOffIcon />}
                      onClick={handleUnlink}
                    >
                      {t('hotelContent.management.vendorHotels.linkModal.unlink')}
                    </LoadingButton>
                  }
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {nimbuxHotel?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {nimbuxHotel?.city}, {nimbuxHotel?.country}
                    </Typography>
                  </Box>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('hotelContent.management.vendorHotels.linkModal.noLinkedNimbuxHotel')}
                </Alert>
              )}

            </Box>
          </>

          {/* Search section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {t('hotelContent.management.vendorHotels.linkModal.linkToNimbuxHotel')}
            </Typography>
            
            <TextField
              size="small"
              fullWidth
              placeholder={t('hotelContent.management.vendorHotels.linkModal.searchPlaceholder')}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Nimbux Hotels list */}
          <Box sx={{ height: 'calc(100% - 300px)', overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
              {t('hotelContent.management.vendorHotels.linkModal.availableNimbuxHotels')}
            </Typography>
            {isLoadingNimbuxHotels ? (
              <LoadingSkeleton />
            ) : (
              <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
                {!nimbuxHotels?.items.length ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: theme.palette.text.disabled,
                    }}
                  >
                    <Typography variant="body2">
                      {t('hotelContent.management.vendorHotels.linkModal.noNimbuxHotelsFound')}
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ padding: 0 }}>
                    {nimbuxHotels?.items.map(hotel => renderNimbuxHotelItem(hotel))}
                  </List>
                )}
              </Box>
            )}
          </Box>
          </>
        )}
      </WideModalBox>
    </Modal>
  );
};